import classNames from "classnames";
import * as React from "react";

import { useDateTimeFormat } from "@/shared/datetime";

import type { Activity, GetActivity } from "./activity";
import { SelectionAnnouncement } from "./SelectionAnnouncement";
import styles from "./Timeline.module.css";
import { TimelineActivity } from "./TimelineActivity";
import {
  ActivityGate,
  DataGate,
  TimelineBoundary,
  TimelineFailed,
  TimelineSkeleton,
  TracksFailed,
  TracksSkeleton,
} from "./TimelineBoundaries";
import { TimelineContext, type TimelineContextValue } from "./TimelineContext";
import { TimelineControls } from "./TimelineControls";
import {
  type FetchItems,
  type GetNeighbor,
  type TimelineDataStreamer,
  useTimelineData,
} from "./TimelineDataStreamer";
import { TimelineFooter } from "./TimelineFooter";
import type { TimelineTrackItem } from "./TimelineTrackItem";
import { TimelineTracks } from "./TimelineTracks";
import { useNeighborSteps } from "./useNeighborSteps";
import { useTimelineHotkeys } from "./useTimelineHotkeys";
import { useTimelineSources } from "./useTimelineSources";
import { useTimelineState } from "./useTimelineState";
import { displayZone, type TimeWindow, type TimeZoneMode } from "./window";

export interface TimelineProps<T> {
  /**
   * Fetch the timeline's items for a @type {TimeWindow}
   */
  getData: FetchItems<T>;

  /**
   * Fetch the whole history's extent and its activity, split into the
   * number of buckets the timeline asks for
   */
  getActivity: GetActivity;

  /** Total number of items on the timeline */
  totalCount: number;

  /**
   * The window to open on (uncontrolled): read when a history loads, and the
   * timeline owns the window from then on. Each new `getActivity` loads a new
   * history and opens on this again, so a page that wants new data to start
   * at the default clears it. Defaults to the last 30 days of the extent.
   */
  initialTimeWindow?: TimeWindow;

  /** Called after every window change, not on the initial one. */
  onTimeWindowChange?: (window: TimeWindow) => void;

  /** The clock every time is read on, as a controlled value. Defaults to `"local"`. */
  tz?: TimeZoneMode;

  /**
   * Called when the user switches the Local/UTC toggle. The toggle is shown
   * only when this is given; without it the timeline reads times on `tz`
   * with no way to change it.
   */
  onTzChange?: (tz: TimeZoneMode) => void;

  /**
   * The selected item as a controlled value. The item, not its id: the
   * streamer only holds the chunk it has loaded, and a selection made
   * elsewhere (a list, a deep link) may lie outside it — the timeline moves
   * the window to show it.
   */
  selectedItem?: TimelineTrackItem<T> | null;

  /** Callback fired when a new track item has been selected */
  onSelect?: (item: TimelineTrackItem<T> | null) => void;

  /**
   * Find the previous / next item across the whole history, for the prev /
   * next buttons, the ← → keys and the empty span's "Jump to nearest". Pass
   * one when the loaded chunk may not hold every item; without it those
   * controls search only the loaded chunk. Keep it stable (`useCallback`),
   * like `getData`: a new identity means the answers have changed (new
   * filters, say), so a step still in flight is dropped.
   */
  getNeighbor?: GetNeighbor<T>;

  /**
   * Shown in place of the whole timeline when the history holds no items,
   * usually a `TimelineMessage` with the page's way to add some. Without it
   * an empty history draws empty tracks.
   */
  empty?: React.ReactNode;

  /**
   * Imperative handle for consumers that need to drive the view.
   */
  timelineRef?: React.Ref<TimelineHandle>;

  /**
   * Page-wide keyboard shortcuts (← → previous / next, Shift+← → pan,
   * + − zoom, F fit all), listening on the document. For the one timeline a
   * page is about; off by default so a timeline inside a form or a dialog
   * does not claim the page's keys. Footer hints appear with them.
   */
  hotkeys?: boolean;
}

export interface TimelineHandle {
  /**
   * Set the timeline's time window, clamped to the history's extent. Set
   * once the history has loaded; until then the handle is not attached.
   */
  setTimeWindow: (window: TimeWindow) => void;
}

/**
 * Component to view items arranged in a timeline
 */
export function Timeline<T>(props: TimelineProps<T>) {
  const { streamer, activityLoader, history, updating } = useTimelineSources(
    props.getData,
    props.getActivity,
    props.initialTimeWindow,
  );

  return (
    <TimelineBoundary
      renderError={(retry) => <TimelineFailed onRetry={retry} />}
      fallback={<TimelineSkeleton />}
      onRetry={() => activityLoader?.retry()}
      source={activityLoader}
    >
      <ActivityGate loader={activityLoader}>
        {/* Below a successful gate `activityLoader` and `history` are set; their checks are for the type checker. `streamer` is a real check: it is null until useStreamer's effect runs. */}
        {activityLoader !== null &&
          history !== null &&
          streamer !== null &&
          (history.itemCount === 0 && props.empty !== undefined ? (
            props.empty
          ) : (
            // Keyed on the loader: new data (a new `getActivity`) mounts a
            // new body, which opens on its own window. A retry keeps the key.
            <TimelineBody
              key={activityLoader.id}
              {...props}
              history={history}
              streamer={streamer}
              updating={updating}
            />
          ))}
      </ActivityGate>
    </TimelineBoundary>
  );
}

interface TimelineBodyProps<T> extends TimelineProps<T> {
  /** The loaded history: its extent bounds the window, and its buckets draw the strip. */
  history: Activity;
  /** The items source the tracks read, for the window on screen. */
  streamer: TimelineDataStreamer<T>;
  /** Newer data is loading behind what is on screen; dims the strip, tracks and footer. */
  updating: boolean;
}

/**
 * The timeline for one loaded history: the window, the actions that move it,
 * the keys, and the sections that draw it. The history is known from the
 * first render, so the window opens from it directly.
 */
function TimelineBody<T>({
  history,
  streamer,
  updating,
  ...props
}: TimelineBodyProps<T>) {
  const { selectedItem = null, hotkeys = false, tz = "local" } = props;
  const { extent } = history;

  const { timeWindow, setTimeWindow, zoomIn, zoomOut, pan, fitAll, reveal } =
    useTimelineState({
      extent,
      initialTimeWindow: props.initialTimeWindow,
      onTimeWindowChange: props.onTimeWindowChange,
      selectedItem,
    });

  const items = useTimelineData(streamer, timeWindow);
  const format = useDateTimeFormat(displayZone(tz));

  const { step, jumpToNearest, canStep } = useNeighborSteps({
    getNeighbor: props.getNeighbor,
    streamer,
    selectedItem,
    timeWindow,
    onSelect: props.onSelect,
    reveal,
  });

  useTimelineHotkeys({
    enabled: hotkeys,
    step,
    zoomIn,
    zoomOut,
    pan,
    fitAll,
  });

  React.useImperativeHandle(props.timelineRef, () => ({ setTimeWindow }), [
    setTimeWindow,
  ]);

  const value = React.useMemo<TimelineContextValue<T>>(
    () => ({
      timeWindow,
      extent,
      setTimeWindow,
      zoomIn,
      zoomOut,
      fitAll,
      items,
      format,
    }),
    [timeWindow, extent, setTimeWindow, zoomIn, zoomOut, fitAll, items, format],
  );

  return (
    <TimelineContext.Provider value={value}>
      <div className={styles.timeline}>
        <SelectionAnnouncement selectedItem={selectedItem} />
        <TimelineControls
          tz={tz}
          onTzChange={props.onTzChange}
          onStep={step}
          canStep={canStep}
        />
        <div
          className={classNames(styles.content, {
            [styles.updating]: updating,
          })}
          aria-busy={updating}
        >
          <TimelineActivity buckets={history.buckets} />
          <TimelineBoundary
            renderError={(retry, error) => (
              <TracksFailed error={error} onRetry={retry} />
            )}
            fallback={<TracksSkeleton />}
            onRetry={() => streamer.requestData(timeWindow)}
            source={streamer}
          >
            <DataGate streamer={streamer} window={timeWindow}>
              <TimelineTracks
                selectedId={selectedItem?.id ?? null}
                onSelect={props.onSelect}
                totalCount={props.totalCount}
                onJumpToNearest={jumpToNearest}
              />
            </DataGate>
          </TimelineBoundary>
          <TimelineFooter totalCount={props.totalCount} hotkeys={hotkeys} />
        </div>
      </div>
    </TimelineContext.Provider>
  );
}
